/**
 * Strip credentials out of Sentry breadcrumbs before Sentry keeps them.
 *
 * Some addresses are credentials in themselves, and each of the kinds below used to reach Sentry.
 *
 * A Google Cloud Storage signed URL carries its signature in the query string, so the URL
 * itself is a password — and the Sentry SDK records the URL of every request the app makes.
 * Without this, an image upload's URL travels to Sentry attached to a later error. Why that
 * matters and what else depends on it: `design-docs/image-uploads.md`, "The signed upload URL
 * is a credential — keep it out of logs and breadcrumbs".
 *
 * A DiscourseConnect login address carries a payload naming the account plus an HMAC over it, so
 * the address is a working login for that person's community-forum account (#4525). It reaches
 * Sentry by a route no log line controls: the forum sends the user back to our own page with the
 * pair still in the address bar, and Sentry records the page address by itself. See
 * `design-docs/discourse-sso.md`.
 *
 * A sign-in link carries a token that signs the holder in — `signInToken`, `claimToken`, or a
 * coach invite's `coachInviteToken` — and it reaches Sentry the same way: it sits in our own page
 * address while boot uses it (df-7ee895718752). See `design-docs/purchase-deep-linking.md`.
 *
 * One rule here is personal data rather than a credential: the account hint (`account=`) an
 * onboarding email's link carries (#5895). It travels the same route, so it takes the same rule.
 *
 * This module holds no Angular and no Sentry import, so a test can load it on its own and cheaply.
 * Same reasoning as `problem-kind.ts` and `boot-freeze-classifier.ts`. (`app-sentry.ts`, which
 * calls this, can be loaded by a test too since df-106e22b8ff13 — but only through the node
 * environment helper, because its import graph reaches Angular and the Sentry SDK.)
 */

/**
 * `X-Goog-Signature`, the query parameter that makes a Google Cloud Storage signed URL usable.
 *
 * Matched case-insensitively because the parameter's case is not something we control — it is
 * produced by whatever signs the URL, and a case difference must not be the reason a credential
 * slips through.
 *
 * Matching with a pattern rather than lower-casing the text first is load-bearing, not a style
 * choice:
 * `toLowerCase()` can CHANGE A STRING'S LENGTH (`U+0130` becomes two code units), so an index taken
 * from the lower-cased copy does not line up with the original. `redactCredentialQuery` compares each matched
 * signature's position against the query string's, and mixing the two scales made that comparison
 * wrong — in the direction that KEEPS a prefix still holding the credential. Both positions now
 * come from the same string.
 *
 * No `g` flag: a global regex carries `lastIndex` between calls, so a shared one would answer
 * differently depending on what it was asked before.
 */
const GCS_SIGNATURE_PATTERN = /x-goog-signature/i;

/**
 * `X-Amz-Signature`, the query parameter that makes an Amazon S3 presigned URL usable (#6768).
 *
 * A Paddle invoice link is one: anyone holding it can read that coach's invoice, with their name
 * and address, for an hour. The coach billing page opens it in a new tab and never logs it, so no
 * breadcrumb should carry it; this rule is the second layer, in case one ever does. Case-insensitive
 * and non-global for the same reasons as `GCS_SIGNATURE_PATTERN` above.
 */
const S3_SIGNATURE_PATTERN = /x-amz-signature/i;

/**
 * The two query parameters that together make a DiscourseConnect address a login (#4525).
 *
 * Signing into our community forum sends the user to an address carrying `sso`, a base64 payload
 * naming the account, and `sig`, an HMAC over that payload computed with the secret Discourse and
 * we both hold. Either one alone is harmless; the two together ARE the login, which is why the rule
 * below demands both. A rule keyed on `sig` alone would blank the query string of plenty of
 * unrelated addresses — an Azure storage link, a third-party widget — and throw away diagnostics
 * for nothing.
 *
 * The address arrives on the return trip in OUR OWN page's address bar, and Sentry records the page
 * address by itself, in navigation breadcrumbs and on the event. So unlike the signed upload URL,
 * this one cannot be fixed only where it is logged — this rule is the only thing that covers it.
 *
 * `(^|[?&])` anchors each name to the start of a query parameter, so `assign=` does not match `sig`
 * and `passo=` does not match `sso`. Case-insensitive and non-global for the same reasons as
 * `GCS_SIGNATURE_PATTERN` above.
 */
const SSO_PAYLOAD_PATTERN = /(^|[?&])sso=/i;
const SSO_SIGNATURE_PATTERN = /(^|[?&])sig=/i;

/**
 * The query parameters that make a sign-in link a login on their own (df-7ee895718752).
 *
 * `signInToken` is a Firebase custom token: anyone holding it can sign in as that person for an
 * hour, as many times as they like. `claimToken` is a purchase claim token the server swaps for a
 * fresh sign-in, with no record of use, for 30 days. `coachInviteToken` is the claim a coach's
 * invite link carries. All three sit in OUR OWN page address until boot has used them, and boot is
 * when errors are most likely, so Sentry's page address and Mixpanel's `$current_url` would carry
 * them otherwise. Unlike the forum pair, each one alone is the credential, so each is its own rule.
 *
 * Anchored and flagged like the patterns above. The anchor keeps `claimToken=` from matching a
 * longer name that merely ends in it, such as a `purchaseClaimToken=` some other link might carry.
 */
const SIGN_IN_TOKEN_PATTERN = /(^|[?&])signInToken=/i;
const CLAIM_TOKEN_PATTERN = /(^|[?&])claimToken=/i;
const COACH_INVITE_TOKEN_PATTERN = /(^|[?&])coachInviteToken=/i;
const SIGN_IN_LINK_MARKER = '?<query redacted: sign-in link credential>';

/**
 * The account hint an onboarding email's link carries: the address the email was sent to (#5895).
 *
 * Not a credential — it grants nothing — but it is somebody's email address, and it sits in our
 * own page address until boot removes it, which is when Sentry and Mixpanel record that address.
 * It gets the same whole-query drop as the credentials above rather than a value-only blank,
 * because that position logic is the one proven path here, and nothing in that query is worth a
 * second copy of it: campaign attribution comes from the app's own copy of the query, never from
 * the recorded page address. Its own marker, so nobody reading a breadcrumb mistakes it for a
 * leaked credential. Anchored so `myaccount=` does not match.
 */
const ACCOUNT_HINT_PATTERN = /(^|[?&])account=/i;

/**
 * One kind of credential this module knows how to find, and what to say when it removes one.
 *
 * A string matches a rule only when EVERY one of its patterns matches. That is what lets the
 * Discourse rule require a pair while the signed-URL rule needs a single parameter, without either
 * needing its own copy of the position logic in `redactCredentialQuery` — and that position logic
 * is the delicate part, so having one copy of it is the point of this shape.
 */
interface CredentialRule {
  patterns: RegExp[];
  /** What replaces the redacted text. Says something was removed rather than that there never was
   * anything, so nobody debugging concludes the address was bare — and names WHICH rule fired, so a
   * reader can tell an over-redaction from a real one. */
  marker: string;
}

/**
 * Every credential this module removes. Deliberately a short list rather than a registry: each
 * entry is a decision somebody made about a specific credential, and an extension point would
 * invite adding patterns without that thought.
 */
const CREDENTIAL_RULES: CredentialRule[] = [
  { patterns: [GCS_SIGNATURE_PATTERN], marker: '?<query redacted: signed URL>' },
  { patterns: [S3_SIGNATURE_PATTERN], marker: '?<query redacted: signed URL>' },
  { patterns: [SSO_PAYLOAD_PATTERN, SSO_SIGNATURE_PATTERN], marker: '?<query redacted: SSO credential>' },
  { patterns: [SIGN_IN_TOKEN_PATTERN], marker: SIGN_IN_LINK_MARKER },
  { patterns: [CLAIM_TOKEN_PATTERN], marker: SIGN_IN_LINK_MARKER },
  { patterns: [COACH_INVITE_TOKEN_PATTERN], marker: SIGN_IN_LINK_MARKER },
  { patterns: [ACCOUNT_HINT_PATTERN], marker: '?<query redacted: account hint>' },
];

/**
 * How deep into a breadcrumb's data bag the walk goes.
 *
 * A breadcrumb has to survive JSON serialisation to reach Sentry at all, so it is a shallow tree
 * in practice; this bound exists so a cyclic or pathological object cannot spin the walk rather
 * than because any real breadcrumb approaches it.
 */
const MAX_WALK_DEPTH = 6;

/**
 * How many values the walk will look at before it gives up on a single breadcrumb.
 *
 * The depth bound alone stops the walk RECURSING for ever, but not doing too much WORK: a bag that
 * is both cyclic and wide reaches every node by many different paths, so the number of visits grows
 * like (branches per node) to the power of the depth. Twenty object-valued properties per node is
 * 64 million visits. This runs on the UI thread for every breadcrumb, so the budget is what keeps a
 * pathological object from freezing the app. Sentry's own serialiser solves the same problem by
 * remembering what it has already seen; a flat budget is simpler and, unlike remembering, also
 * bounds a wide acyclic object.
 */
const MAX_WALK_VISITS = 5000;

/** What replaces a value the walk did not look inside — because it hit the depth bound or the visit
 * budget. Sentry's own serialiser replaces anything below depth 3 with a placeholder of its own, so
 * in practice nothing readable is lost. */
const UNWALKED_MARKER = '<not inspected for credentials>';

/**
 * Which rule, if any, this text trips.
 *
 * Deliberately keyed on the query parameters that make an address a credential rather than on the
 * hostname. A host rule would have to be kept in step with every bucket and every forum host we
 * ever talk to, and missing one means leaking a credential; the parameter is the thing that
 * actually makes the address usable, wherever it is served from.
 */
function findCredentialRule(text: string): CredentialRule | undefined {
  return CREDENTIAL_RULES.find(rule => rule.patterns.every(pattern => pattern.test(text)));
}

/**
 * Remove the credential from text a rule matched.
 *
 * The WHOLE query goes, not just the parameter we recognised. CLAUDE.md's assume-secret-by-default
 * rule says to redact the class rather than the one field we happen to have in mind, and nothing
 * else in that query is worth the risk of picking through it. What survives — scheme, host and path
 * — is the part with diagnostic value, so the breadcrumb still says which object was being written
 * to or which forum the user was being sent to, and can still be matched against a server log line.
 *
 * The prefix before the first `?` is kept ONLY when EVERY parameter the rule matched comes after
 * that `?`, which is where a real address puts them. Anything else is not an address whose front
 * half we can prove is safe — free text can carry a parameter and a later question mark in either
 * order — so the whole string goes. Losing readability is the cheap direction to be wrong in;
 * leaking the credential is not.
 */
function redactCredentialQuery(text: string, rule: CredentialRule): string {
  // every position is taken from the SAME string, so they are on the same scale — see the note on
  // GCS_SIGNATURE_PATTERN for why lower-casing first made this comparison wrong
  const queryStart = text.indexOf('?');
  const earliestMatch = Math.min(...rule.patterns.map(pattern => text.search(pattern)));
  // if there is no query string, or a matched parameter sits before it, no prefix is safe to keep
  if (queryStart < 0 || earliestMatch < queryStart) {
    return rule.marker;
  }
  return text.slice(0, queryStart) + rule.marker;
}

/**
 * The same rules applied to one plain string, for a caller that has an address in hand rather
 * than a breadcrumb.
 *
 * Written for the Mixpanel send hook (#5151): the SDK's own click and page-leave events carry the
 * page address as `$current_url`, and using this rather than a rule of their own is what keeps the
 * two answers — what Sentry redacts and what Mixpanel redacts — from drifting apart.
 *
 * Text that trips no rule comes back byte for byte, so an ordinary page keeps its full address.
 */
export function redactCredentialsInText(text: string): string {
  const rule = findCredentialRule(text);
  return rule ? redactCredentialQuery(text, rule) : text;
}

/** The parts of a Sentry breadcrumb this module reads. Declared here rather than imported from
 * Sentry so the module stays free of that dependency. */
export interface RedactableBreadcrumb {
  message?: string;
  data?: Record<string, unknown>;
}

/** How much of its budget one breadcrumb's walk has spent. Threaded through the recursion rather
 * than kept in module state so that two overlapping calls could never share a counter. */
interface WalkBudget {
  visitsLeft: number;
}

/**
 * The values Sentry's serialiser treats as errors, which is WIDER than `instanceof Error`.
 *
 * Sentry decides by the object's internal tag rather than by the prototype chain. The case that
 * makes the difference real here is an error created in ANOTHER REALM — an iframe, and this app
 * runs inside an app frame — which carries the right tag but fails `instanceof Error`, because
 * `instanceof` compares against this realm's `Error`. The `Exception` tags cover host objects that
 * are error-shaped without deriving from `Error` at all. (A `DOMException` is NOT an example: web
 * IDL makes it inherit from `Error`, so `instanceof` already catches it. It is in the set because
 * Sentry has it, and matching Sentry exactly is the point.)
 *
 * Matching the narrower test would leave those walked as ordinary objects, which for an error means
 * walked as an EMPTY one, since everything it carries is non-enumerable.
 */
const SENTRY_ERROR_TAGS = new Set([
  '[object Error]',
  '[object Exception]',
  '[object DOMException]',
  '[object WebAssembly.Exception]',
]);

/** Would Sentry's serialiser expand this value as an error? */
function isErrorToSentry(value: object): boolean {
  return SENTRY_ERROR_TAGS.has(Object.prototype.toString.call(value)) || value instanceof Error;
}

/**
 * An error carries its `message` and `stack` as NON-enumerable properties, so `Object.keys` sees
 * nothing and a plain object walk would return it untouched. Sentry does not leave it that way: its
 * serialiser turns an error into a plain object with those fields, so a signed URL inside an
 * error message would be transmitted after this module had declared the breadcrumb clean.
 *
 * Returning the same shape Sentry would have produced keeps the breadcrumb readable and puts the
 * text somewhere the walk can redact. Sentry's version also carries whatever own enumerable
 * properties somebody hung on the error, so this one does too — otherwise an `err.url` holding a
 * signed URL would be transmitted without ever being looked at, and an error that DID need
 * redacting would silently lose its extra fields from the breadcrumb.
 */
function plainObjectForError(error: { name?: string; message?: string; stack?: string }): Record<string, unknown> {
  // the three fields first, then the spread — the named ones are non-enumerable, so the spread
  // cannot overwrite them and there is nothing to lose by letting it try
  return { name: error.name, message: error.message, stack: error.stack, ...error };
}

/**
 * Redact every credential-bearing string reachable inside a value, returning what should replace
 * it.
 *
 * **This NEVER writes into the value it is given.** A container with something redacted below it
 * comes back as a new object or array; one with nothing to redact comes back as the very same
 * reference. That is not a style choice — in production `LoggingService` builds a breadcrumb's
 * data bag with a SHALLOW spread of the arguments somebody logged, so everything below the top
 * level is still the app's own live object. Writing into it would mean a `console.warn('upload
 * failed', uploadState)` silently replacing `uploadState.url` with the redaction marker, and a
 * retry then PUTting to a URL that no longer exists. Writing back also throws on a frozen object
 * or fires an unrelated setter, and `beforeBreadcrumb` has no `catch` around this.
 */
function redactWithin(value: unknown, depth: number, budget: WalkBudget): unknown {
  // if this is a string, it is the only thing that can carry a credential
  if (typeof value === 'string') {
    const rule = findCredentialRule(value);
    return rule ? redactCredentialQuery(value, rule) : value;
  }
  // if this is not a container, there is nothing below it to walk
  if (value === null || typeof value !== 'object') {
    return value;
  }
  // if we have gone as deep as we walk, or spent the whole budget, replace the container rather
  // than pass it on unexamined. A cycle is the case that matters: following `self` six times lands
  // back on the caller's own object, so RETURNING it here would put the unredacted signed URL back
  // into the result we hand Sentry. Failing closed at our own limits keeps the promise this module
  // makes without depending on Sentry's serialiser truncating first.
  if (depth >= MAX_WALK_DEPTH || budget.visitsLeft <= 0) {
    return UNWALKED_MARKER;
  }
  budget.visitsLeft--;

  // for an array, redact into a copy and keep the copy only if an element actually changed
  if (Array.isArray(value)) {
    const arrayCopy: unknown[] = [];
    let arrayChanged = false;
    for (const item of value) {
      const redacted = redactWithin(item, depth + 1, budget);
      if (redacted !== item) arrayChanged = true;
      arrayCopy.push(redacted);
    }
    return arrayChanged ? arrayCopy : value;
  }

  // if this is an error, walk the plain shape Sentry will turn it into — its text is invisible to
  // Object.keys, so walking it as an ordinary object would declare it clean without reading it
  if (isErrorToSentry(value)) {
    const asPlain = plainObjectForError(value);
    const redacted = redactWithin(asPlain, depth, budget);
    // if nothing in it needed redacting, hand the original error back so nothing else changes
    return redacted === asPlain ? value : redacted;
  }

  // otherwise do the same for the object's own values
  const bag = value as Record<string, unknown>;
  const copy: Record<string, unknown> = {};
  let changed = false;
  for (const key of Object.keys(bag)) {
    const redacted = redactWithin(bag[key], depth + 1, budget);
    if (redacted !== bag[key]) changed = true;
    copy[key] = redacted;
  }
  return changed ? copy : bag;
}

/**
 * Redact any credential-bearing text in a breadcrumb, in place.
 *
 * BOTH the `message` and the whole of the `data` bag are covered, nested values included. What that
 * does and does not reach is written up once, in `design-docs/image-uploads.md`, "The signed upload
 * URL is a credential — keep it out of logs and breadcrumbs".
 *
 * The ONLY thing written to is the breadcrumb's own `message` and `data` fields — the breadcrumb
 * object belongs to this call and mutating it matches how `beforeSend` in `app-sentry.ts` already
 * handles the event it is given. Nothing nested inside `data` is ever written to; a redacted bag
 * is a fresh object. `redactWithin` says why that distinction is load-bearing.
 */
export function redactCredentialsInBreadcrumb<T extends RedactableBreadcrumb>(breadcrumb: T): T {
  // if the breadcrumb carries a message, redact that too — it is not part of the data bag
  if (typeof breadcrumb.message === 'string') {
    const rule = findCredentialRule(breadcrumb.message);
    if (rule) breadcrumb.message = redactCredentialQuery(breadcrumb.message, rule);
  }
  // if there is a data bag, swap in a redacted copy — but only when something in it changed
  if (breadcrumb.data && typeof breadcrumb.data === 'object') {
    const redacted = redactWithin(breadcrumb.data, 0, { visitsLeft: MAX_WALK_VISITS });
    if (redacted !== breadcrumb.data) {
      breadcrumb.data = redacted as Record<string, unknown>;
    }
  }
  return breadcrumb;
}
